<script>
    import ibOverflow from './ib-overflow.vue';
    import $util from '../../global/util';

    const transDuration = 250;

    /**
     * This is a bar that contains one or more tabs, each of which has an associated value, route, or link.
     * It reacts to changes in the `value` prop and emits `input` events, allowing it to be used with `v-model`.
     * Overflow is handled by [ib-overflow](#/components/layout/ib-overflow) and displays arrows or allows touch
     * panning depending on device. The active tab is visually distinguished from the other tabs; by default this
     * is denoted by an underline.
     *
     * Clear tabs do not use a marker to denote the active tab and instead use text weight and opacity.
     *
     * Note: `ib-tab-bar` is designed to animate the transition between tabs (except with clear appearance). If
     * you are not seeing the transition, it is likely because your component structure is causing the tab bar to
     * be destroyed and recreated in reaction to a change in selected tab. If this is the case, please adjust the
     * structure of your components to not do this.
     *
     * @see Each tab is represented as an instance of [ib-tab](#/components/layout/ib-tab).
     */
    export default {
        name: 'ib-tab-bar',

        props: {
            /**
             * The key of the tab to display. If not specified, the first tab will be shown. If the value
             * does not match the key of any tab, no tab will be selected.
             */
            value: [String,Number],
            /**
             * Appearance modifier. `clear` is supported.
             */
            appearance: String,
            /**
             * Horizontal alignment: `start`, `center`, `end`, or `grow`.
             */
            align: {
                type: String,
                default: 'start'
            },
            /**
             * Tab density. Supported are `compact` and `comfortable`.
             */
            spacing: String,
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height in any CSS units.
             */
            height: String
        },

        data() {
            return {
                tr: 0,
                ts: $util.transform({
                    tx: 0,
                    sx: 1
                }),
                tone: void 0
            };
        },

        computed: {
            cls() {
                return {
                    '_tabtr': this.tr,
                    ['_e'+this.appearance]: this.appearance,
                    ['_a'+this.align]: this.align,
                    ['_s'+this.spacing]: this.spacing
                }
            }
        },

        watch: {
            value: 'selectvalue'
        },

        beforeCreate() {
            this.isTabs = true;
            this.tabs = [];
        },

        created() {
            const parent = this.$parent;
            if (parent.isTabContainer) {
                parent.bar = this;
                this.con = parent;
            }
        },

        beforeDestroy() {
            const con = this.con;
            if (con) {
                con.bar = void 0;
            }
            this.tr && clearTimeout(this.tr);
        },

        render(h) {
            // vue-docgen-api
            if (0) {
                /**
                 * @slot This slot accepts one or more [ib-tab](#/components/layout/ib-tab) objects.
                 */
                const d = this.$slots.default;
            }

            const vm = this;
            return h(ibOverflow, {
                'ref': 'ovf',
                'staticClass': '_tabs2',
                'class': vm.cls,
                'style': {
                    'width': vm.width,
                    'height': vm.height
                },
                'attrs': {
                    'role': 'tablist'
                }
            }, [
                vm._t('default'),
                vm.appearance != 'clear'
                    ? h('div', {
                        'staticClass': '_tabu2',
                        'class': vm.tone ? '_taba-' + vm.tone : void 0,
                        'style': vm.ts
                    })
                    : vm._e()
            ]);
        },

        methods: {
            sync() {
                if (!this.willParse) {
                    this.willParse = true;
                    this.$nextTick(this.parseNodes);
                }
            },

            parseNodes() {
                const tabs = (this.$slots.default || []).filter(vnode =>
                    vnode.componentOptions?.Ctor.options.name === 'ib-tab' ||
                    vnode.componentOptions?.Ctor.options.name === 'ib-tab2');
                this.tabs = tabs;
                const active = this.active;
                if (active == null || !tabs.some(tab => tab.componentInstance === active)) {
                    const value = this.value;
                    this.select(tabs.find(tab => {
                        const propsData = tab.componentOptions.propsData;
                        return (value == null || value == tab.key) &&
                            !(propsData.to || propsData.href || propsData.disabled || propsData.disabled === '');
                    })?.componentInstance, true);
                }
                this.willParse = false;
                if (!this.ready) {
                    this.ready = true;
                    this.$nextTick(this.showActive);
                }
            },

            selectvalue(value, last, con) {
                this.select(this.tabs.find(tab => tab.key == value)?.componentInstance, false, con);
            },

            select(tab, emit, con) {
                const active = this.active;
                if (active !== tab) {
                    let value;
                    if (active) {
                        active.leave();
                    }
                    if (tab) {
                        tab.enter();
                        value = tab.$vnode.key;
                    }
                    if (tab && active && this.appearance != 'clear') {
                        const oel = active.$el;
                        const nel = tab.$el;
                        if (oel && nel) {
                            this.ts = {
                                left: oel.offsetLeft + 'px',
                                width: oel.offsetWidth + 'px',
                                ...$util.transform({
                                    tx: nel.offsetLeft - oel.offsetLeft,
                                    sx: nel.clientWidth / oel.clientWidth
                                })
                            };
                            this.tr && clearTimeout(this.tr);
                            this.tr = setTimeout(() => {
                                this.tr = 0;
                                this.ts = $util.transform({
                                    tx: 0,
                                    sx: 1
                                });
                            }, transDuration);
                        }
                        this.tone = tab.tone;
                    }
                    if (emit && value !== this.value) {
                        this.$emit('input', value);
                    }
                    if (this.con && value != null && !con) {
                        this.con.$nextTick(() => {
                            this.con.selectvalue(value);
                        });
                    }
                    this.active = tab;
                    this.showActive('smooth');
                }
            },

            showActive(behavior) {
                const active = this.active;
                const ovf = this.$refs.ovf;
                if (active && ovf) {
                    $util.scrollIntoView(
                        active.$el,
                        ovf.$refs.main,
                        void 0,
                        {
                            left: 32,
                            right: 32
                        },
                        behavior
                    );
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tabs2 {
        font-weight: 500;
        color: @fg-heading;

        > ._ovfs > ._btn,
        > ._ovfe > ._btn {
            border-bottom: @tab-border solid transparent;
        }
        > ._ovfm {
            display: flex;
            flex-direction: row;
            & when not (@tab-outer-padding = none) {
                padding: @tab-outer-padding;
            }
        }
        &._astart {
            > ._ovfm {
                justify-content: flex-start;
            }
        }
        &._acenter {
            > ._ovfm {
                justify-content: center;
            }
        }
        &._aend {
            > ._ovfm {
                justify-content: flex-end;
            }
        }
        &._agrow {
            ._tab {
                flex: 1 1 auto;
            }
        }
        &._eclear {
            > ._ovfm {
                & when not (@tab-outer-padding = none) {
                    padding: 0;
                }
            }
        }
    }
    ._tabu2 {
        position: absolute;
        bottom: 0;
        border-bottom: @tab-border solid @fg-accent;
        visibility: hidden;
        will-change: transform;
        transform-origin: left;
    }
    ._taba {
        border-bottom-color: @fg-accent;
    }
    each(@tone, {
        @fg-var: 'fg-@{value}';
        ._taba-@{value} {
            border-bottom-color: @@fg-var;
        }
    });
    ._tabtr {
        ._tab {
            border-bottom-color: transparent !important;
        }
        ._tabu2 {
            visibility: visible;
            transition:
                transform .2s cubic-bezier(.2,.6,.4,1),
                border-bottom-color .2s linear;
        }
    }
</style>

<ib-example id="clear" name="Clear tabs with v-model">
    <template lang="pug">
        div
            ib-tab-bar(appearance="clear", v-model="value")
                ib-tab(key="1D", disabled) 1D
                ib-tab(key="7D") 7D
                ib-tab(key="MTD") MTD
                ib-tab(key="1M") 1M
                ib-tab(key="3M") 3M
                ib-tab(key="1Y") 1Y
            p.before-32 Value: 
                ib-field(id="value", v-model="value")
    </template>
    <script>
        {
            data() {
                return {
                    value: '7D'
                };
            }
        }
    </script>
</ib-example>

<ib-example id="index" name="By Index">
    <template lang="pug">
        div
            ib-tab-bar(v-model="value")
                ib-tab(v-for="i in 32", :key="i") Tab {{i}}
            p.before-32 Value: 
                ib-field-numeric(id="value", v-model="value")
    </template>
    <script>
        {
            data() {
                return {
                    value: 1
                };
            }
        }
    </script>
</ib-example>

<ib-example id="tone" name="Tone">
    <template lang="pug">
        ib-tab-bar
            ib-tab(key="buy", tone="buy") Buy
            ib-tab(key="sell", tone="sell") Sell
    </template>
</ib-example>
